import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, Plus, Pencil, Trash2, GripVertical, Check, X, AlertTriangle, Settings, Boxes, Bug, Wrench, Package, FileText, DollarSign, ListTree, Radio, ShieldAlert, Droplets, ListTodo, CheckCircle2, Clock, Zap, Video, Play, Download, BookOpen, ShoppingBag, Calendar, Users, ClipboardList, Upload } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tab configurations
const CONFIG_TABS = [
  { id: 'hive_types', name: 'Hive Types', shortName: 'Hives', icon: Boxes, description: 'Hive configurations' },
  { id: 'bee_varieties', name: 'Bee Varieties', shortName: 'Bees', icon: Bug, description: 'Bee breeds and species' },
  { id: 'tools', name: 'Tools', shortName: 'Tools', icon: Wrench, description: 'Apiary tools & equipment' },
  { id: 'components', name: 'Components', shortName: 'Components', icon: Package, description: 'Hive components & parts' },
  { id: 'product_types', name: 'Product Types', shortName: 'Products', icon: ShoppingBag, description: 'Product categories for sale', isCustom: true },
  { id: 'origin_types', name: 'Origin Types', shortName: 'Origin', icon: ListTree, description: 'How bees were acquired' },
  { id: 'inspection_fields', name: 'Inspection Fields', shortName: 'Fields', icon: FileText, description: 'Custom inspection fields' },
  { id: 'happening_now', name: 'Happening Now', shortName: 'Activity', icon: Radio, description: 'Live activity feed types' },
  { id: 'critical_alerts', name: 'Critical Alerts', shortName: 'Alerts', icon: ShieldAlert, description: 'Dashboard alert triggers' },
  { id: 'task_types', name: 'Task Types', shortName: 'Tasks', icon: ListTodo, description: 'Future reminder tasks', isCustom: true },
  { id: 'action_types', name: 'Action Types', shortName: 'Actions', icon: Zap, description: 'Actions taken during inspections', isCustom: true },
  { id: 'demo_videos', name: 'Demo Videos', shortName: 'Videos', icon: Video, description: 'Login page demo videos', isCustom: true },
  { id: 'beekeeper_tasks', name: 'Beekeeper Tasks', shortName: 'Bee Tasks', icon: ClipboardList, description: 'Custom tasks created by beekeepers (read-only)', isCustom: true },
  { id: 'challenges', name: 'DIY Challenges', shortName: 'Challenges', icon: Zap, description: 'Manage monthly challenges and bonus events', isCustom: true },
];

// Default box types for common hive types
const DEFAULT_BOX_OPTIONS = [
  { name: 'Deep Brood', sizes: ['8', '9', '10'], max_qty: 4 },
  { name: 'Deep Super', sizes: ['8', '9', '10'], max_qty: 4 },
  { name: 'Medium Super', sizes: ['8', '9', '10'], max_qty: 6 },
  { name: 'Shallow Super', sizes: ['8', '9', '10'], max_qty: 6 },
  { name: 'Ideal Super', sizes: ['8', '9', '10'], max_qty: 6 },
  { name: 'Flow Super', sizes: ['6', '7'], max_qty: 2 },
];

// Field configurations for each type
const FIELD_CONFIGS = {
  hive_types: [
    { key: 'name', label: 'Name', type: 'text', required: true },
    { key: 'description', label: 'Description', type: 'textarea' },
    { key: 'category', label: 'Category', type: 'select', options: ['standard', 'specialty', 'custom'] },
    { key: 'requires_boxes', label: 'Requires Stackable Boxes', type: 'boolean', helpText: 'Enable for hive types like Langstroth that use multiple stackable boxes' },
    { key: 'box_types', label: 'Box Types', type: 'box_config', dependsOn: 'requires_boxes' },
    { key: 'frame_types', label: 'Frame Types', type: 'toggle_group', options: ['Wooden', 'Plastic', '21st Century', 'BeeWise', 'Small Cell', 'Specialty', 'Flow Frames', 'Hoffman', 'Manley', 'SN4', 'SN5', 'DN4', 'DN5', 'Ideal'] },
    { key: 'foundation_types', label: 'Foundation Types', type: 'toggle_group', options: ['Wax', 'Plastic', 'Waxed Plastic', 'Specialised Frame', 'Strips', 'Foundationless', 'Small Cell Wax', 'Drone Cell', 'Thin Surplus'] },
    { key: 'notes', label: 'Notes', type: 'textarea' },
  ],
  bee_varieties: [
    { key: 'name', label: 'Name', type: 'text', required: true },
    { key: 'scientific_name', label: 'Scientific Name', type: 'text' },
    { key: 'origin', label: 'Origin', type: 'text' },
    { key: 'temperament', label: 'Temperament', type: 'select', options: ['gentle', 'moderate', 'defensive', 'varies'] },
    { key: 'productivity', label: 'Productivity', type: 'select', options: ['low', 'medium', 'high'] },
    { key: 'traits', label: 'Traits (comma separated)', type: 'text', isArray: true },
    { key: 'description', label: 'Description', type: 'textarea' },
  ],
  tools: [
    { key: 'name', label: 'Name', type: 'text', required: true },
    { key: 'description', label: 'Description', type: 'textarea' },
    { key: 'section', label: 'Section (in Apiary form)', type: 'select', options: ['protective_gear', 'hive_tools', 'feeding_equipment', 'heavy_equipment', 'miscellaneous'], required: true },
    { key: 'category', label: 'Category', type: 'select', options: ['general', 'protective', 'maintenance'] },
    { key: 'default_price', label: 'Default Price', type: 'number' },
    { key: 'unit', label: 'Unit', type: 'select', options: ['each', 'pair', 'set'] },
  ],
  components: [
    { key: 'name', label: 'Name', type: 'text', required: true },
    { key: 'description', label: 'Description', type: 'textarea' },
    { key: 'section', label: 'Section (in Edit Hive form)', type: 'select', options: ['lids_covers', 'bottom_boards', 'feeders', 'queen_management', 'pest_control', 'heavy_accessories'], required: true },
    { key: 'category', label: 'Category', type: 'select', options: ['lids', 'feeders', 'boards', 'excluders', 'pest_control', 'other'] },
    { key: 'default_price', label: 'Default Price', type: 'number' },
  ],
  origin_types: [
    { key: 'name', label: 'Name', type: 'text', required: true },
    { key: 'description', label: 'Description', type: 'textarea' },
    { key: 'requires_seller', label: 'Requires Seller Info', type: 'boolean' },
    { key: 'requires_date', label: 'Requires Date', type: 'boolean' },
    { key: 'requires_price', label: 'Requires Price', type: 'boolean' },
  ],
  inspection_fields: [
    { key: 'name', label: 'Field Name', type: 'text', required: true },
    { key: 'field_type', label: 'Field Type', type: 'select', options: ['text', 'number', 'dropdown', 'multi_select', 'yes_no', 'date'], required: true },
    { key: 'section', label: 'Section', type: 'select', options: ['general', 'queen', 'brood', 'resources', 'pests', 'custom'] },
    { key: 'options', label: 'Options (comma separated, for dropdown/multi-select)', type: 'text', isArray: true },
    { key: 'min_value', label: 'Min Value (for number)', type: 'number' },
    { key: 'max_value', label: 'Max Value (for number)', type: 'number' },
    { key: 'required', label: 'Required Field', type: 'boolean' },
    { key: 'placeholder', label: 'Placeholder Text', type: 'text' },
    { key: 'help_text', label: 'Help Text', type: 'textarea' },
  ],
  happening_now: [
    { key: 'name', label: 'Activity Name', type: 'text', required: true, placeholder: 'e.g., Harvesting, Feeding' },
    { key: 'description', label: 'Description', type: 'textarea', placeholder: 'What this activity means' },
    { key: 'icon', label: 'Icon (Emoji)', type: 'text', placeholder: '🍯' },
    { key: 'color', label: 'Color Theme', type: 'select', options: ['amber', 'blue', 'red', 'green', 'purple', 'pink', 'orange', 'teal'] },
    { key: 'field_name', label: 'Database Field Name', type: 'text', required: true, placeholder: 'e.g., currently_harvesting (snake_case)' },
    { key: 'map_visible', label: 'Show on Map Filter', type: 'boolean' },
    { key: 'sort_order', label: 'Sort Order', type: 'number' },
  ],
  critical_alerts: [
    { key: 'name', label: 'Alert Name', type: 'text', required: true },
    { key: 'category', label: 'Category', type: 'select', options: ['entrance', 'condition', 'brood', 'queen', 'population', 'swarm', 'disease', 'pest', 'resources'], required: true },
    { key: 'field_name', label: 'Inspection Field', type: 'text', required: true, placeholder: 'e.g., activity_at_entrance' },
    { key: 'trigger_values', label: 'Trigger Values (comma separated)', type: 'text', isArray: true, required: true, placeholder: 'e.g., None, Low' },
    { key: 'severity', label: 'Severity', type: 'select', options: ['critical', 'warning', 'info'], required: true },
    { key: 'is_enabled', label: 'Enabled (Show on Dashboard)', type: 'boolean' },
    { key: 'sort_order', label: 'Sort Order', type: 'number' },
  ],
};

// Harvest Configuration Panel Component
const HarvestConfigPanel = ({ config, setConfig, loading, saving, onSave }) => {
  const [newEquipmentName, setNewEquipmentName] = useState('');
  const [showAddEquipment, setShowAddEquipment] = useState(false);

  // Initialize harvesting equipment from config (without useEffect to avoid cascading renders)
  const harvestingEquipment = config?.harvesting_equipment && Array.isArray(config.harvesting_equipment) 
    ? config.harvesting_equipment 
    : [
        { id: 'extractors', name: 'Honey Extractors', key: 'extractors', qty: 0, price: 0 },
        { id: 'uncapping_knives', name: 'Uncapping Knives', key: 'uncapping_knives', qty: 0, price: 0 },
        { id: 'uncapping_forks', name: 'Uncapping Forks', key: 'uncapping_forks', qty: 0, price: 0 },
        { id: 'uncapping_tanks', name: 'Uncapping Tanks', key: 'uncapping_tanks', qty: 0, price: 0 },
        { id: 'honey_buckets', name: 'Honey Buckets', key: 'honey_buckets', qty: 0, price: 0 },
        { id: 'strainers', name: 'Strainers/Filters', key: 'strainers', qty: 0, price: 0 },
        { id: 'bottling_tanks', name: 'Bottling Tanks', key: 'bottling_tanks', qty: 0, price: 0 },
        { id: 'bottling_equipment', name: 'Bottling Equipment', key: 'bottling_equipment', qty: 0, price: 0 },
        { id: 'honey_gates', name: 'Honey Gates', key: 'honey_gates', qty: 0, price: 0 },
        { id: 'refractometers', name: 'Refractometers', key: 'refractometers', qty: 0, price: 0 },
        { id: 'bee_escapes', name: 'Bee Escapes', key: 'bee_escapes', qty: 0, price: 0 },
        { id: 'fume_boards', name: 'Fume Boards', key: 'fume_boards', qty: 0, price: 0 },
        { id: 'capping_scratchers', name: 'Capping Scratchers', key: 'capping_scratchers', qty: 0, price: 0 },
        { id: 'honey_warmers', name: 'Honey Warmers', key: 'honey_warmers', qty: 0, price: 0 },
        { id: 'wax_melters', name: 'Wax Melters', key: 'wax_melters', qty: 0, price: 0 },
      ];

  // Update config when equipment changes
  const updateEquipment = (id, updates) => {
    const updated = harvestingEquipment.map(e => e.id === id ? { ...e, ...updates } : e);
    setConfig(prev => ({ ...prev, harvesting_equipment: updated }));
  };

  const addEquipment = () => {
    if (!newEquipmentName.trim()) return;
    const newItem = {
      id: Date.now().toString(),
      name: newEquipmentName.trim(),
      key: newEquipmentName.trim().toLowerCase().replace(/\s+/g, '_'),
      qty: 0,
      price: 0,
      is_custom: true
    };
    const updated = [...harvestingEquipment, newItem];
    setConfig(prev => ({ ...prev, harvesting_equipment: updated }));
    setNewEquipmentName('');
    setShowAddEquipment(false);
  };

  const removeEquipment = (id) => {
    if (!window.confirm('Remove this equipment?')) return;
    const updated = harvestingEquipment.filter(e => e.id !== id);
    setConfig(prev => ({ ...prev, harvesting_equipment: updated }));
  };

  if (loading || !config) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center text-gray-500">Loading harvest configuration...</div>
        </CardContent>
      </Card>
    );
  }

  const updateConfig = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const addHoneyType = () => {
    const newType = { id: Date.now().toString(), name: '', color: 'amber', is_active: true };
    updateConfig('honey_types', [...(config.honey_types || []), newType]);
  };

  const updateHoneyType = (id, updates) => {
    updateConfig('honey_types', config.honey_types.map(t => t.id === id ? { ...t, ...updates } : t));
  };

  const removeHoneyType = (id) => {
    updateConfig('honey_types', config.honey_types.filter(t => t.id !== id));
  };

  const addQualityGrade = () => {
    const newGrade = { id: Date.now().toString(), name: '', description: '', is_active: true };
    updateConfig('quality_grades', [...(config.quality_grades || []), newGrade]);
  };

  const updateQualityGrade = (id, updates) => {
    updateConfig('quality_grades', config.quality_grades.map(g => g.id === id ? { ...g, ...updates } : g));
  };

  const removeQualityGrade = (id) => {
    updateConfig('quality_grades', config.quality_grades.filter(g => g.id !== id));
  };

  const addStorageLocation = () => {
    const newLoc = { id: Date.now().toString(), name: '', is_active: true };
    updateConfig('storage_locations', [...(config.storage_locations || []), newLoc]);
  };

  const updateStorageLocation = (id, updates) => {
    updateConfig('storage_locations', config.storage_locations.map(l => l.id === id ? { ...l, ...updates } : l));
  };

  const removeStorageLocation = (id) => {
    updateConfig('storage_locations', config.storage_locations.filter(l => l.id !== id));
  };

  return (
    <div className="space-y-6">
      {/* General Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Droplets className="w-5 h-5 text-amber-600" />
            Harvest Settings
          </CardTitle>
          <CardDescription>Configure your harvest tracking preferences</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Season Start Month */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label className="font-medium">Harvest Season Start Month</Label>
              <p className="text-sm text-gray-500 mb-2">When does your harvest year begin?</p>
              <Select 
                value={config.harvest_year_start_month?.toString() || "9"} 
                onValueChange={(v) => updateConfig('harvest_year_start_month', parseInt(v))}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select month..." />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map(month => (
                    <SelectItem key={month.value} value={month.value.toString()}>
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="font-medium">Default Weight Unit</Label>
              <p className="text-sm text-gray-500">Each beekeeper sets their own unit in Weights → General Settings. It applies to hive weights, harvests, analytics and PDFs.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Honey Types */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">🍯 Honey Types & Pricing</CardTitle>
            <CardDescription>Types of honey you harvest with sale prices per unit</CardDescription>
          </div>
          <Button onClick={addHoneyType} variant="outline" size="sm">
            <Plus className="w-4 h-4 mr-1" /> Add Type
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {(config.honey_types || []).map((type, index) => (
              <div key={type.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <Input
                    value={type.name}
                    onChange={(e) => updateHoneyType(type.id, { name: e.target.value })}
                    placeholder="Honey type name..."
                    className="mb-2"
                  />
                </div>
                <div className="w-28">
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={type.price_per_unit || ''}
                      onChange={(e) => updateHoneyType(type.id, { price_per_unit: parseFloat(e.target.value) || 0 })}
                      placeholder="0.00"
                      className="pl-6 text-right"
                    />
                  </div>
                  <span className="text-xs text-gray-400 block text-center">per {config.default_unit || 'kg'}</span>
                </div>
                <Select value={type.color || 'amber'} onValueChange={(v) => updateHoneyType(type.id, { color: v })}>
                  <SelectTrigger className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="amber">🟡 Amber</SelectItem>
                    <SelectItem value="gold">🟠 Gold</SelectItem>
                    <SelectItem value="yellow">🟨 Yellow</SelectItem>
                    <SelectItem value="brown">🟤 Brown</SelectItem>
                    <SelectItem value="dark">⚫ Dark</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={type.is_active !== false}
                    onCheckedChange={(v) => updateHoneyType(type.id, { is_active: v })}
                  />
                  <span className="text-xs text-gray-500">{type.is_active !== false ? 'Active' : 'Inactive'}</span>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => removeHoneyType(type.id)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            {(!config.honey_types || config.honey_types.length === 0) && (
              <p className="text-center text-gray-500 py-4">No honey types configured. Add some to get started.</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Quality Grades */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">⭐ Quality Grades</CardTitle>
            <CardDescription>Quality classifications for your honey</CardDescription>
          </div>
          <Button onClick={addQualityGrade} variant="outline" size="sm">
            <Plus className="w-4 h-4 mr-1" /> Add Grade
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {(config.quality_grades || []).map((grade, index) => (
              <div key={grade.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Input
                    value={grade.name}
                    onChange={(e) => updateQualityGrade(grade.id, { name: e.target.value })}
                    placeholder="Grade name (e.g., Premium)..."
                  />
                  <Input
                    value={grade.description || ''}
                    onChange={(e) => updateQualityGrade(grade.id, { description: e.target.value })}
                    placeholder="Description..."
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={grade.is_active !== false}
                    onCheckedChange={(v) => updateQualityGrade(grade.id, { is_active: v })}
                  />
                  <span className="text-xs text-gray-500">{grade.is_active !== false ? 'Active' : 'Inactive'}</span>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => removeQualityGrade(grade.id)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            {(!config.quality_grades || config.quality_grades.length === 0) && (
              <p className="text-center text-gray-500 py-4">No quality grades configured. Add some to get started.</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Storage Locations */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">📦 Storage Locations</CardTitle>
            <CardDescription>Where you store your harvested honey</CardDescription>
          </div>
          <Button onClick={addStorageLocation} variant="outline" size="sm">
            <Plus className="w-4 h-4 mr-1" /> Add Location
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {(config.storage_locations || []).map((loc, index) => (
              <div key={loc.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <Input
                    value={loc.name}
                    onChange={(e) => updateStorageLocation(loc.id, { name: e.target.value })}
                    placeholder="Storage location name..."
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={loc.is_active !== false}
                    onCheckedChange={(v) => updateStorageLocation(loc.id, { is_active: v })}
                  />
                  <span className="text-xs text-gray-500">{loc.is_active !== false ? 'Active' : 'Inactive'}</span>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => removeStorageLocation(loc.id)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            {(!config.storage_locations || config.storage_locations.length === 0) && (
              <p className="text-center text-gray-500 py-4">No storage locations configured. Add some to get started.</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Harvesting Equipment */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">🍶 Harvesting Equipment</CardTitle>
            <CardDescription>Equipment used for extracting and processing honey. Set prices for inventory tracking.</CardDescription>
          </div>
          <Button onClick={() => setShowAddEquipment(true)} variant="outline" size="sm">
            <Plus className="w-4 h-4 mr-1" /> Add Equipment
          </Button>
        </CardHeader>
        <CardContent>
          {showAddEquipment && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex gap-2">
                <Input
                  value={newEquipmentName}
                  onChange={(e) => setNewEquipmentName(e.target.value)}
                  placeholder="Equipment name..."
                  className="flex-1"
                  onKeyDown={(e) => e.key === 'Enter' && addEquipment()}
                />
                <Button onClick={addEquipment} size="sm" className="bg-amber-600 hover:bg-amber-700">Add</Button>
                <Button onClick={() => { setShowAddEquipment(false); setNewEquipmentName(''); }} variant="outline" size="sm">Cancel</Button>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {harvestingEquipment.map((equip) => (
              <div key={equip.id} className={`flex items-center gap-3 p-3 rounded-lg ${equip.is_custom ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'}`}>
                <div className="flex-1">
                  <div className="font-medium text-stone-700">{equip.name}</div>
                  {equip.is_custom && <span className="text-xs text-amber-600">Custom</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-stone-500">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={equip.price || ''}
                    onChange={(e) => updateEquipment(equip.id, { price: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                    className="w-24 h-8 text-sm text-right"
                  />
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => removeEquipment(equip.id)}
                  className="text-red-400 hover:text-red-600 opacity-70 hover:opacity-100"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
          {harvestingEquipment.length === 0 && (
            <p className="text-center text-gray-500 py-4">No harvesting equipment configured.</p>
          )}
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={onSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
          {saving ? 'Saving...' : 'Save Harvest Settings'}
        </Button>
      </div>

      {/* Info card */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="pt-4">
          <div className="flex gap-3">
            <div className="text-amber-600">🍯</div>
            <div className="text-sm text-amber-800">
              <p className="font-medium mb-1">How harvest tracking works:</p>
              <ul className="list-disc list-inside space-y-1 text-amber-700">
                <li>The harvest year runs from your selected start month (e.g., Sep 2025 - Aug 2026)</li>
                <li>Honey types, quality grades, and storage locations appear in the harvest recording form</li>
                <li>Track moisture content to ensure honey quality (ideal: below 18.5%)</li>
                <li>View harvest history and totals from the Dashboard under &quot;Harvests&quot;</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Tools Configuration Panel - Shows all default apiary tools by category
const ToolsConfigPanel = ({ token, loading: parentLoading, items, onRefresh }) => {
  const [addingToSection, setAddingToSection] = useState(null);
  const [newToolName, setNewToolName] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customCategories, setCustomCategories] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [acceptedSuggestionId, setAcceptedSuggestionId] = useState(null);
  const [movingTool, setMovingTool] = useState(null);
  const API = process.env.REACT_APP_BACKEND_URL;

  // Fetch custom categories and suggestions
  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [catRes, sugRes] = await Promise.all([
          axios.get(`${API}/api/config/custom-categories/tools`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/api/suggestions?type=tool&status=pending`, { headers }).catch(() => ({ data: [] }))
        ]);
        setCustomCategories(catRes.data || []);
        setSuggestions(sugRes.data || []);
      } catch (err) {
      }
    };
    if (token) fetchData();
  }, [token, API]);

  // Section labels and colors
  const sectionLabels = {
    'protective_gear': 'Protective Gear',
    'hive_tools': 'Hive Tools',
    'feeding_equipment': 'Feeding Equipment',
    'harvesting_equipment': 'Harvesting Equipment',
    'heavy_equipment': 'Heavy Equipment',
    'miscellaneous': 'Miscellaneous',
  };

  const sectionColors = {
    'protective_gear': { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', icon: '🦺' },
    'hive_tools': { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', icon: '🔧' },
    'feeding_equipment': { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800', icon: '🍯' },
    'harvesting_equipment': { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-800', icon: '🧴' },
    'heavy_equipment': { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-800', icon: '🚜' },
    'miscellaneous': { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-800', icon: '📦' },
  };

  // Group tools by section from database items
  const toolsBySection = {};
  (items || []).forEach(item => {
    const section = item.section || 'miscellaneous';
    if (!toolsBySection[section]) {
      toolsBySection[section] = [];
    }
    toolsBySection[section].push(item);
  });

  // Add custom categories
  customCategories.forEach(cat => {
    if (!toolsBySection[cat.key]) {
      toolsBySection[cat.key] = [];
    }
    sectionLabels[cat.key] = cat.name;
    sectionColors[cat.key] = { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-800', icon: '✨' };
  });

  const handleAddTool = async (section) => {
    if (!newToolName.trim()) return;
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      // Use admin config endpoint for consistency
      await axios.post(`${API}/api/admin/config/tools`, {
        name: newToolName.trim(),
        section: section,
        key: newToolName.trim().toLowerCase().replace(/\s+/g, '_'),
        is_active: true,
        is_default: false
      }, { headers });
      
      // If this was from an accepted suggestion, update the suggestion status
      if (acceptedSuggestionId) {
        try {
          await axios.put(`${API}/api/suggestions/${acceptedSuggestionId}`, { status: 'accepted' }, { headers });
          setSuggestions(prev => prev.filter(s => s.id !== acceptedSuggestionId));
        } catch (err) {
        }
        setAcceptedSuggestionId(null);
      }
      
      toast.success('Tool added successfully');
      setNewToolName('');
      setAddingToSection(null);
      onRefresh();
    } catch (err) {
      toast.error('Failed to add tool');
    }
    setSaving(false);
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/api/config/custom-categories`, {
        name: newCategoryName.trim(),
        type: 'tools',
        key: newCategoryName.trim().toLowerCase().replace(/\s+/g, '_')
      }, { headers });
      toast.success('Category added successfully');
      setNewCategoryName('');
      setShowNewCategory(false);
      // Refresh categories
      const catRes = await axios.get(`${API}/api/config/custom-categories/tools`, { headers });
      setCustomCategories(catRes.data || []);
    } catch (err) {
      toast.error('Failed to add category');
    }
    setSaving(false);
  };

  const handleDeleteTool = async (toolId) => {
    if (!window.confirm('Remove this tool?')) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      // Use admin config endpoint for consistency
      await axios.delete(`${API}/api/admin/config/tools/${toolId}`, { headers });
      toast.success('Tool deleted');
      onRefresh();
    } catch (err) {
      // Handle default item error
      if (err.response?.data?.detail?.includes('default')) {
        toast.error('Default tools cannot be deleted. Use the toggle to deactivate instead.');
      } else {
        toast.error('Failed to delete tool');
      }
    }
  };

  const handleToggleTool = async (toolId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/api/admin/config/tools/${toolId}/toggle`, {}, { headers });
      toast.success('Tool status updated');
      onRefresh();
    } catch (err) {
      toast.error('Failed to update tool status');
    }
  };

  const handleMoveTool = async (toolId, newSection) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/api/admin/config/tools/${toolId}/move`, { section: newSection }, { headers });
      toast.success('Tool moved');
      setMovingTool(null);
      onRefresh();
    } catch (err) {
      toast.error('Failed to move tool');
    }
  };

  // All available sections for move dropdown
  const allSections = Object.keys(sectionLabels);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-amber-600" />
              Apiary Tools Configuration
              {suggestions.length > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {suggestions.length} suggestion{suggestions.length > 1 ? 's' : ''}
                </span>
              )}
            </CardTitle>
            <CardDescription>Click + on any category to add a custom tool</CardDescription>
          </div>
          <Button onClick={() => setShowNewCategory(true)} variant="outline" className="border-purple-300 text-purple-700 hover:bg-purple-50">
            <Plus className="w-4 h-4 mr-2" />
            New Category
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* New Category Form */}
          {showNewCategory && (
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
              <h4 className="font-semibold text-purple-800 mb-3">✨ Create New Category</h4>
              <div className="flex gap-2">
                <Input
                  placeholder="Category name..."
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  className="flex-1"
                />
                <Button onClick={handleAddCategory} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
                  {saving ? 'Adding...' : 'Add'}
                </Button>
                <Button onClick={() => { setShowNewCategory(false); setNewCategoryName(''); }} variant="outline">
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Suggestions Section */}
          {suggestions.length > 0 && (
            <div className="bg-amber-50 border border-amber-300 rounded-xl p-4">
              <h4 className="font-semibold text-amber-800 mb-3">💡 User Suggestions ({suggestions.length})</h4>
              <div className="space-y-2">
                {suggestions.map(sug => (
                  <div key={sug.id} className={`bg-white rounded-lg px-3 py-2 flex justify-between items-center border ${acceptedSuggestionId === sug.id ? 'border-green-400 bg-green-50' : 'border-amber-200'}`}>
                    <div>
                      <span className="font-medium">{sug.name}</span>
                      <span className="text-xs text-stone-500 ml-2">suggested by {sug.suggested_by_email}</span>
                      {sug.suggested_section && (
                        <span className="text-xs text-amber-600 ml-2">→ {sectionLabels[sug.suggested_section] || sug.suggested_section}</span>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="h-7 text-green-600 border-green-300" onClick={() => {
                        setNewToolName(sug.name);
                        setAddingToSection(sug.suggested_section || 'hive_tools');
                        setAcceptedSuggestionId(sug.id);
                      }}>
                        Accept
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-red-600 border-red-300" onClick={async () => {
                        try {
                          const headers = { Authorization: `Bearer ${token}` };
                          await axios.put(`${API}/api/suggestions/${sug.id}`, { status: 'rejected' }, { headers });
                          setSuggestions(prev => prev.filter(s => s.id !== sug.id));
                          toast.success('Suggestion rejected');
                        } catch (err) {
                          toast.error('Failed to reject suggestion');
                        }
                      }}>
                        Reject
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {Object.entries(toolsBySection).map(([section, tools]) => {
            const colors = sectionColors[section] || sectionColors.miscellaneous;
            const isCustomCategory = customCategories.some(c => c.key === section);
            
            return (
              <div key={section} className={`${colors.bg} ${colors.border} border rounded-xl p-4`}>
                <div className="flex justify-between items-center mb-3">
                  <h4 className={`font-semibold ${colors.text}`}>
                    {colors.icon} {sectionLabels[section] || section}
                    {isCustomCategory && <span className="text-xs ml-2">(custom)</span>}
                    <span className="text-xs ml-2 font-normal">({tools.length})</span>
                  </h4>
                  <div className="flex items-center gap-1">
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className={`h-7 ${colors.text}`}
                      onClick={() => setAddingToSection(addingToSection === section ? null : section)}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-red-400 hover:text-red-600 hover:bg-red-50"
                      onClick={async () => {
                        if (!window.confirm(`Delete entire "${sectionLabels[section] || section}" category and all ${tools.length} items?`)) return;
                        try {
                          await axios.delete(`${API}/api/admin/config/tools/section/${section}`, { headers: { Authorization: `Bearer ${token}` } });
                          toast.success('Category deleted');
                          onRefresh();
                        } catch (err) { toast.error('Failed to delete category'); }
                      }}
                      title="Delete entire category"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                
                {/* Add Tool Form */}
                {addingToSection === section && (
                  <div className="flex gap-2 mb-3">
                    <Input
                      placeholder="New tool name..."
                      value={newToolName}
                      onChange={(e) => setNewToolName(e.target.value)}
                      className="flex-1 bg-white"
                      onKeyDown={(e) => e.key === 'Enter' && handleAddTool(section)}
                    />
                    <Button onClick={() => handleAddTool(section)} disabled={saving} size="sm" className="bg-amber-600 hover:bg-amber-700">
                      {saving ? '...' : 'Add'}
                    </Button>
                    <Button onClick={() => { setAddingToSection(null); setNewToolName(''); setAcceptedSuggestionId(null); }} variant="outline" size="sm">
                      Cancel
                    </Button>
                  </div>
                )}
                
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                  {tools.map(tool => (
                    <div 
                      key={tool.id || tool.key} 
                      className={`bg-white rounded-lg px-3 py-2 text-sm border flex justify-between items-center group ${
                        tool.is_active === false ? 'border-stone-300 bg-stone-50 opacity-60' : 'border-stone-200 text-stone-700'
                      }`}
                    >
                      <span className={`truncate ${tool.is_active === false ? 'line-through text-stone-400' : ''}`}>
                        {tool.name}
                        {tool.form_key && <span className="text-[10px] text-stone-400 ml-1 font-mono">[{tool.form_key}]</span>}
                      </span>
                      <div className="flex items-center gap-1 flex-shrink-0 ml-1">
                        {/* Move button */}
                        <div className="relative">
                          <button
                            onClick={() => setMovingTool(movingTool === tool.id ? null : tool.id)}
                            className="text-blue-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity p-0.5"
                            title="Move to another category"
                          >
                            <GripVertical className="w-3 h-3" />
                          </button>
                          {movingTool === tool.id && (
                            <div className="absolute right-0 top-6 z-50 bg-white border border-stone-200 rounded-lg shadow-lg py-1 min-w-[160px]">
                              {allSections.filter(s => s !== section).map(s => (
                                <button key={s} className="w-full text-left px-3 py-1.5 text-xs hover:bg-stone-100 text-stone-700" onClick={() => handleMoveTool(tool.id, s)}>
                                  {sectionLabels[s] || s}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        {/* Toggle */}
                        <button 
                          onClick={() => handleToggleTool(tool.id)}
                          className={`text-xs px-1.5 py-0.5 rounded ${
                            tool.is_active !== false 
                              ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                              : 'bg-stone-200 text-stone-500 hover:bg-stone-300'
                          }`}
                          title={tool.is_active !== false ? 'Click to deactivate' : 'Click to activate'}
                        >
                          {tool.is_active !== false ? 'On' : 'Off'}
                        </button>
                        {/* Delete */}
                        <button 
                          onClick={() => handleDeleteTool(tool.id)}
                          className="text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity p-0.5"
                          title="Delete tool"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {tools.length === 0 && (
                    <div className="col-span-4 text-sm text-stone-400 text-center py-2">
                      No tools in this category
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
};

// Components Configuration Panel - Shows all default hive components by category  
const ComponentsConfigPanel = ({ token, loading: parentLoading, items, onRefresh }) => {
  const [addingToSection, setAddingToSection] = useState(null);
  const [newComponentName, setNewComponentName] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customCategories, setCustomCategories] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [acceptedSuggestionId, setAcceptedSuggestionId] = useState(null);
  const [movingComponent, setMovingComponent] = useState(null);
  const API = process.env.REACT_APP_BACKEND_URL;

  // Fetch custom categories and suggestions
  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [catRes, sugRes] = await Promise.all([
          axios.get(`${API}/api/config/custom-categories/components`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/api/suggestions?type=component&status=pending`, { headers }).catch(() => ({ data: [] }))
        ]);
        setCustomCategories(catRes.data || []);
        setSuggestions(sugRes.data || []);
      } catch (err) {
      }
    };
    if (token) fetchData();
  }, [token, API]);

  // Section labels and colors
  const sectionLabels = {
    'lids_covers': 'Lids & Covers',
    'feeders': 'Feeders',
    'queen_bee_management': 'Queen Management',
    'bottom_boards_stands': 'Bottom Boards & Stands',
    'pest_control': 'Pest Control',
    'heavy_accessories': 'Heavy & Accessories',
    'miscellaneous': 'Miscellaneous',
  };

  const sectionColors = {
    'lids_covers': { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', icon: '🎩' },
    'feeders': { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', icon: '🍯' },
    'queen_bee_management': { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-800', icon: '👑' },
    'bottom_boards_stands': { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800', icon: '📦' },
    'pest_control': { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', icon: '🛡️' },
    'heavy_accessories': { bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-800', icon: '⚙️' },
    'miscellaneous': { bg: 'bg-stone-50', border: 'border-stone-200', text: 'text-stone-800', icon: '📋' },
  };

  // Group components by section from database items
  const componentsBySection = {};
  (items || []).forEach(item => {
    const section = item.section || 'miscellaneous';
    if (!componentsBySection[section]) {
      componentsBySection[section] = [];
    }
    componentsBySection[section].push(item);
  });

  // Add custom categories
  customCategories.forEach(cat => {
    if (!componentsBySection[cat.key]) {
      componentsBySection[cat.key] = [];
    }
    sectionLabels[cat.key] = cat.name;
    sectionColors[cat.key] = { bg: 'bg-pink-50', border: 'border-pink-200', text: 'text-pink-800', icon: '✨' };
  });

  const handleAddComponent = async (section) => {
    if (!newComponentName.trim()) return;
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      // Use the admin config endpoint to ensure component shows up in the admin list
      await axios.post(`${API}/api/admin/config/components`, {
        name: newComponentName.trim(),
        section: section,
        key: newComponentName.trim().toLowerCase().replace(/\s+/g, '_'),
        is_active: true,
        is_default: false
      }, { headers });
      
      // If this was from an accepted suggestion, update the suggestion status
      if (acceptedSuggestionId) {
        try {
          await axios.put(`${API}/api/suggestions/${acceptedSuggestionId}`, { status: 'accepted' }, { headers });
          // Remove from local suggestions list
          setSuggestions(prev => prev.filter(s => s.id !== acceptedSuggestionId));
        } catch (err) {
        }
        setAcceptedSuggestionId(null);
      }
      
      toast.success('Component added successfully');
      setNewComponentName('');
      setAddingToSection(null);
      onRefresh();
    } catch (err) {
      toast.error('Failed to add component');
    }
    setSaving(false);
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/api/config/custom-categories`, {
        name: newCategoryName.trim(),
        type: 'components',
        key: newCategoryName.trim().toLowerCase().replace(/\s+/g, '_')
      }, { headers });
      toast.success('Category added successfully');
      setNewCategoryName('');
      setShowNewCategory(false);
      // Refresh categories
      const catRes = await axios.get(`${API}/api/config/custom-categories/components`, { headers });
      setCustomCategories(catRes.data || []);
    } catch (err) {
      toast.error('Failed to add category');
    }
    setSaving(false);
  };

  const handleDeleteComponent = async (componentId) => {
    if (!window.confirm('Remove this component?')) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/api/admin/config/components/${componentId}`, { headers });
      toast.success('Component deleted');
      onRefresh();
    } catch (err) {
      // Handle default item error
      if (err.response?.data?.detail?.includes('default')) {
        toast.error('Default components cannot be deleted. Use the toggle to deactivate instead.');
      } else {
        toast.error('Failed to delete component');
      }
    }
  };

  const handleToggleComponent = async (componentId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/api/admin/config/components/${componentId}/toggle`, {}, { headers });
      toast.success('Component status updated');
      onRefresh();
    } catch (err) {
      toast.error('Failed to update component status');
    }
  };

  const handleMoveComponent = async (componentId, newSection) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/api/admin/config/components/${componentId}/move`, { section: newSection }, { headers });
      toast.success('Component moved');
      setMovingComponent(null);
      onRefresh();
    } catch (err) {
      toast.error('Failed to move component');
    }
  };

  const allSections = Object.keys(sectionLabels);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-amber-600" />
              Hive Components Configuration
              {suggestions.length > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {suggestions.length} suggestion{suggestions.length > 1 ? 's' : ''}
                </span>
              )}
            </CardTitle>
            <CardDescription>Click + on any category to add a custom component</CardDescription>
          </div>
          <Button onClick={() => setShowNewCategory(true)} variant="outline" className="border-pink-300 text-pink-700 hover:bg-pink-50">
            <Plus className="w-4 h-4 mr-2" />
            New Category
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* New Category Form */}
          {showNewCategory && (
            <div className="bg-pink-50 border border-pink-200 rounded-xl p-4">
              <h4 className="font-semibold text-pink-800 mb-3">✨ Create New Category</h4>
              <div className="flex gap-2">
                <Input
                  placeholder="Category name..."
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  className="flex-1"
                />
                <Button onClick={handleAddCategory} disabled={saving} className="bg-pink-600 hover:bg-pink-700">
                  {saving ? 'Adding...' : 'Add'}
                </Button>
                <Button onClick={() => { setShowNewCategory(false); setNewCategoryName(''); }} variant="outline">
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Suggestions Section */}
          {suggestions.length > 0 && (
            <div className="bg-amber-50 border border-amber-300 rounded-xl p-4">
              <h4 className="font-semibold text-amber-800 mb-3">💡 User Suggestions ({suggestions.length})</h4>
              <div className="space-y-2">
                {suggestions.map(sug => (
                  <div key={sug.id} className={`bg-white rounded-lg px-3 py-2 flex justify-between items-center border ${acceptedSuggestionId === sug.id ? 'border-green-400 bg-green-50' : 'border-amber-200'}`}>
                    <div>
                      <span className="font-medium">{sug.name}</span>
                      <span className="text-xs text-stone-500 ml-2">suggested by {sug.suggested_by_email}</span>
                      {sug.suggested_section && (
                        <span className="text-xs text-amber-600 ml-2">→ {sectionLabels[sug.suggested_section] || sug.suggested_section}</span>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="h-7 text-green-600 border-green-300" onClick={() => {
                        setNewComponentName(sug.name);
                        setAddingToSection(sug.suggested_section || 'feeders');
                        setAcceptedSuggestionId(sug.id);
                      }}>
                        Accept
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-red-600 border-red-300" onClick={async () => {
                        try {
                          const headers = { Authorization: `Bearer ${token}` };
                          await axios.put(`${API}/api/suggestions/${sug.id}`, { status: 'rejected' }, { headers });
                          setSuggestions(prev => prev.filter(s => s.id !== sug.id));
                          toast.success('Suggestion rejected');
                        } catch (err) {
                          toast.error('Failed to reject suggestion');
                        }
                      }}>
                        Reject
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {Object.entries(componentsBySection).map(([section, components]) => {
            const colors = sectionColors[section] || sectionColors.miscellaneous;
            const isCustomCategory = customCategories.some(c => c.key === section);
            
            return (
              <div key={section} className={`${colors.bg} ${colors.border} border rounded-xl p-4`}>
                <div className="flex justify-between items-center mb-3">
                  <h4 className={`font-semibold ${colors.text}`}>
                    {colors.icon} {sectionLabels[section] || section}
                    {isCustomCategory && <span className="text-xs ml-2">(custom)</span>}
                    <span className="text-xs ml-2 font-normal">({components.length})</span>
                  </h4>
                  <div className="flex items-center gap-1">
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className={`h-7 ${colors.text}`}
                      onClick={() => setAddingToSection(addingToSection === section ? null : section)}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-red-400 hover:text-red-600 hover:bg-red-50"
                      onClick={async () => {
                        if (!window.confirm(`Delete entire "${sectionLabels[section] || section}" category and all ${components.length} items?`)) return;
                        try {
                          await axios.delete(`${API}/api/admin/config/components/section/${section}`, { headers: { Authorization: `Bearer ${token}` } });
                          toast.success('Category deleted');
                          onRefresh();
                        } catch (err) { toast.error('Failed to delete category'); }
                      }}
                      title="Delete entire category"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                
                {/* Add Component Form */}
                {addingToSection === section && (
                  <div className="flex gap-2 mb-3">
                    <Input
                      placeholder="New component name..."
                      value={newComponentName}
                      onChange={(e) => setNewComponentName(e.target.value)}
                      className="flex-1 bg-white"
                      onKeyDown={(e) => e.key === 'Enter' && handleAddComponent(section)}
                    />
                    <Button onClick={() => handleAddComponent(section)} disabled={saving} size="sm" className="bg-amber-600 hover:bg-amber-700">
                      {saving ? '...' : 'Add'}
                    </Button>
                    <Button onClick={() => { setAddingToSection(null); setNewComponentName(''); setAcceptedSuggestionId(null); }} variant="outline" size="sm">
                      Cancel
                    </Button>
                  </div>
                )}
                
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                  {components.map(comp => (
                    <div 
                      key={comp.id || comp.key} 
                      className={`bg-white rounded-lg px-3 py-2 text-sm border flex justify-between items-center group ${
                        comp.is_active === false ? 'border-stone-300 bg-stone-50 opacity-60' : 'border-stone-200 text-stone-700'
                      }`}
                    >
                      <span className={`truncate ${comp.is_active === false ? 'line-through text-stone-400' : ''}`}>
                        {comp.name}
                        {comp.form_key && <span className="text-[10px] text-stone-400 ml-1 font-mono">[{comp.form_key}]</span>}
                      </span>
                      <div className="flex items-center gap-1 flex-shrink-0 ml-1">
                        {/* Move button */}
                        <div className="relative">
                          <button
                            onClick={() => setMovingComponent(movingComponent === comp.id ? null : comp.id)}
                            className="text-blue-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity p-0.5"
                            title="Move to another category"
                          >
                            <GripVertical className="w-3 h-3" />
                          </button>
                          {movingComponent === comp.id && (
                            <div className="absolute right-0 top-6 z-50 bg-white border border-stone-200 rounded-lg shadow-lg py-1 min-w-[160px]">
                              {allSections.filter(s => s !== section).map(s => (
                                <button key={s} className="w-full text-left px-3 py-1.5 text-xs hover:bg-stone-100 text-stone-700" onClick={() => handleMoveComponent(comp.id, s)}>
                                  {sectionLabels[s] || s}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        {/* Toggle */}
                        <button 
                          onClick={() => handleToggleComponent(comp.id)}
                          className={`text-xs px-1.5 py-0.5 rounded ${
                            comp.is_active !== false 
                              ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                              : 'bg-stone-200 text-stone-500 hover:bg-stone-300'
                          }`}
                          title={comp.is_active !== false ? 'Click to deactivate' : 'Click to activate'}
                        >
                          {comp.is_active !== false ? 'On' : 'Off'}
                        </button>
                        {/* Delete */}
                        <button 
                          onClick={() => handleDeleteComponent(comp.id)}
                          className="text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity p-0.5"
                          title="Delete component"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {components.length === 0 && (
                    <div className="col-span-4 text-sm text-stone-400 text-center py-2">
                      No components in this category
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
};

// Product Types Configuration Panel (Admin manages product categories)
const ProductTypesConfigPanel = ({ token, loading: parentLoading }) => {
  const [productTypes, setProductTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    icon: '📦',
    description: '',
    is_active: true
  });

  // Default product type icons to choose from
  const AVAILABLE_ICONS = [
    '📦', '👑', '🐝', '🏠', '🔧', '🍯', '🕯️', '🧪', '🌼', '🥜', 
    '🧴', '💊', '📋', '🛠️', '🪵', '🧰', '🎁', '🏷️', '💰', '📊'
  ];

  useEffect(() => {
    fetchProductTypes();
  }, []);

  const fetchProductTypes = async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API_URL}/api/admin/config/product_types`, { headers });
      if (res.ok) {
        const data = await res.json();
        setProductTypes(data.items || []);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Type name is required');
      return;
    }

    setSaving(true);
    try {
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` })
      };

      const url = editingType 
        ? `${API_URL}/api/admin/config/product_types/${editingType.id}`
        : `${API_URL}/api/admin/config/product_types`;
      
      const method = editingType ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success(editingType ? 'Product type updated' : 'Product type added');
        fetchProductTypes();
        setShowAddDialog(false);
        setEditingType(null);
        setFormData({ name: '', icon: '📦', description: '', is_active: true });
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to save');
      }
    } catch (err) {
      toast.error('Failed to save product type');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (typeId) => {
    if (!window.confirm('Delete this product type? Products using this type will need to be reassigned.')) return;

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await fetch(`${API_URL}/api/admin/config/product_types/${typeId}`, { method: 'DELETE', headers });
      toast.success('Product type deleted');
      fetchProductTypes();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleToggle = async (typeId, currentStatus) => {
    try {
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` })
      };
      await fetch(`${API_URL}/api/admin/config/product_types/${typeId}/toggle`, {
        method: 'PUT',
        headers
      });
      fetchProductTypes();
    } catch (err) {
      toast.error('Failed to toggle status');
    }
  };

  const openEditDialog = (type) => {
    setEditingType(type);
    setFormData({
      name: type.name || '',
      icon: type.icon || '📦',
      description: type.description || '',
      is_active: type.is_active !== false
    });
    setShowAddDialog(true);
  };

  const openAddDialog = () => {
    setEditingType(null);
    setFormData({ name: '', icon: '📦', description: '', is_active: true });
    setShowAddDialog(true);
  };

  if (loading || parentLoading) {
    return <div className="text-center py-8 text-gray-500">Loading product types...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Product Type Categories</h3>
          <p className="text-sm text-gray-500">Define product categories that users can add products under</p>
        </div>
        <Button onClick={openAddDialog} className="bg-amber-600 hover:bg-amber-700">
          <Plus className="w-4 h-4 mr-2" />
          Add Type
        </Button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
        <p className="font-medium">💡 How it works:</p>
        <p className="mt-1">Product types you create here will be available for users to categorize their products. Users can add and manage their own products under Operations → Products.</p>
      </div>

      {/* Product Types Grid */}
      <div className="grid gap-3">
        {productTypes.length === 0 ? (
          <Card className="p-8 text-center text-gray-500">
            <ShoppingBag className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No product types configured yet.</p>
            <p className="text-sm mt-1">Add types like "Nuc", "Queen", "Wax", "Propolis", etc.</p>
          </Card>
        ) : (
          productTypes.map(type => (
            <Card key={type.id} className={`${!type.is_active ? 'opacity-60' : ''}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="text-3xl">{type.icon || '📦'}</div>
                  <div>
                    <div className="font-medium text-gray-800 flex items-center gap-2">
                      {type.name}
                      {!type.is_active && (
                        <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">Inactive</span>
                      )}
                    </div>
                    {type.description && (
                      <p className="text-sm text-gray-500 mt-0.5">{type.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={type.is_active !== false}
                    onCheckedChange={() => handleToggle(type.id, type.is_active)}
                  />
                  <Button variant="ghost" size="sm" onClick={() => openEditDialog(type)}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(type.id)} className="text-red-500 hover:text-red-700">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingType ? 'Edit Product Type' : 'Add Product Type'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Icon Selection */}
            <div>
              <Label>Icon</Label>
              <div className="grid grid-cols-10 gap-2 mt-2">
                {AVAILABLE_ICONS.map(icon => (
                  <button
                    key={icon}
                    onClick={() => setFormData({ ...formData, icon })}
                    className={`p-2 rounded-lg border-2 text-xl transition-all ${
                      formData.icon === icon 
                        ? 'border-amber-500 bg-amber-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {icon}
                  </button>
                ))}
              </div>
            </div>

            {/* Type Name */}
            <div>
              <Label>Type Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Nuc, Queen, Wax, Propolis"
                className="mt-1"
              />
            </div>

            {/* Description */}
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description for this product type"
                className="mt-1"
                rows={2}
              />
            </div>

            {/* Active Toggle */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <Label className="text-sm font-medium">Active</Label>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
                {saving ? 'Saving...' : editingType ? 'Update' : 'Add Type'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Task Types Configuration Panel
const TaskTypesPanel = ({ token, loading: parentLoading }) => {
  const [taskTypes, setTaskTypes] = useState([]);
  const [pendingTasks, setPendingTasks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newTaskName, setNewTaskName] = useState('');
  const [newTaskCategory, setNewTaskCategory] = useState('other');

  useEffect(() => {
    fetchTaskTypes();
  }, []);

  const fetchTaskTypes = async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API_URL}/api/task-types?include_pending=true`, { headers });
      const data = await res.json();
      setTaskTypes(data.items || []);
      setPendingTasks(data.pending || []);
      setCategories(data.categories || []);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleAddTask = async () => {
    if (!newTaskName.trim()) {
      toast.error('Please enter a task name');
      return;
    }
    
    setSaving(true);
    try {
      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      const res = await fetch(`${API_URL}/api/task-types`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ name: newTaskName.trim(), category: newTaskCategory })
      });
      const data = await res.json();
      toast.success(data.message || 'Task added!');
      setNewTaskName('');
      setNewTaskCategory('other');
      fetchTaskTypes();
    } catch (err) {
      toast.error('Failed to add task');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (taskId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await fetch(`${API_URL}/api/task-types/${taskId}/approve`, {
        method: 'POST',
        headers
      });
      toast.success('Task approved!');
      fetchTaskTypes();
    } catch (err) {
      toast.error('Failed to approve');
    }
  };

  const handleReject = async (taskId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await fetch(`${API_URL}/api/task-types/${taskId}/reject`, {
        method: 'POST',
        headers
      });
      toast.success('Task rejected');
      fetchTaskTypes();
    } catch (err) {
      toast.error('Failed to reject');
    }
  };

  const handleToggleActive = async (taskId, isActive) => {
    try {
      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      await fetch(`${API_URL}/api/task-types/${taskId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ is_active: !isActive })
      });
      fetchTaskTypes();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (taskId) => {
    if (!confirm('Delete this task type?')) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await fetch(`${API_URL}/api/task-types/${taskId}`, {
        method: 'DELETE',
        headers
      });
      toast.success('Task deleted');
      fetchTaskTypes();
    } catch (err) {
      toast.error('Cannot delete default tasks');
    }
  };

  const getCategoryColor = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    const colors = {
      red: 'bg-red-100 text-red-800',
      blue: 'bg-blue-100 text-blue-800',
      amber: 'bg-amber-100 text-amber-800',
      purple: 'bg-purple-100 text-purple-800',
      gray: 'bg-gray-100 text-gray-800',
      green: 'bg-green-100 text-green-800',
      orange: 'bg-orange-100 text-orange-800',
      yellow: 'bg-yellow-100 text-yellow-800',
      pink: 'bg-pink-100 text-pink-800',
      stone: 'bg-stone-100 text-stone-800'
    };
    return colors[cat?.color] || colors.stone;
  };

  if (loading || parentLoading) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center text-gray-500">Loading task types...</div>
        </CardContent>
      </Card>
    );
  }

  // Group tasks by category
  const tasksByCategory = {};
  taskTypes.forEach(task => {
    const cat = task.category || 'other';
    if (!tasksByCategory[cat]) tasksByCategory[cat] = [];
    tasksByCategory[cat].push(task);
  });

  return (
    <div className="space-y-6">
      {/* Pending Approval Section */}
      {pendingTasks.length > 0 && (
        <Card className="border-2 border-amber-400 bg-amber-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-800">
              <Clock className="w-5 h-5" />
              Pending Approval ({pendingTasks.length})
            </CardTitle>
            <CardDescription className="text-amber-700">
              User-submitted tasks waiting for your approval
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pendingTasks.map(task => (
                <div key={task.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200">
                  <div>
                    <span className="font-medium">{task.name}</span>
                    <span className="text-xs text-gray-500 ml-2">by {task.submitted_by_name}</span>
                    <Badge className={`ml-2 ${getCategoryColor(task.category)}`}>
                      {categories.find(c => c.id === task.category)?.name || task.category}
                    </Badge>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleApprove(task.id)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleReject(task.id)}
                      className="text-red-600 border-red-300 hover:bg-red-50"
                    >
                      <X className="w-4 h-4 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add New Task */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ListTodo className="w-5 h-5 text-purple-600" />
            Task Types
          </CardTitle>
          <CardDescription>
            Configure available tasks for inspections and reminders
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3 mb-6">
            <Input
              value={newTaskName}
              onChange={(e) => setNewTaskName(e.target.value)}
              placeholder="New task name..."
              className="flex-1"
            />
            <Select value={newTaskCategory} onValueChange={setNewTaskCategory}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={handleAddTask} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
              <Plus className="w-4 h-4 mr-1" /> Add Task
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Task List by Category */}
      {categories.map(category => {
        const tasks = tasksByCategory[category.id] || [];
        if (tasks.length === 0) return null;
        
        return (
          <Card key={category.id}>
            <CardHeader className="py-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Badge className={getCategoryColor(category.id)}>{category.name}</Badge>
                <span className="text-sm text-gray-500">({tasks.length} tasks)</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {tasks.map(task => (
                  <div 
                    key={task.id} 
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      task.is_active === false ? 'bg-gray-50 opacity-60' : 'bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={task.is_active === false ? 'line-through text-gray-400' : ''}>
                        {task.name}
                      </span>
                      {task.is_default && (
                        <Badge variant="outline" className="text-xs">Default</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={task.is_active !== false}
                        onCheckedChange={() => handleToggleActive(task.id, task.is_active !== false)}
                      />
                      {!task.is_default && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(task.id)}
                          className="text-red-500 hover:text-red-700 h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Info Card */}
      <Card className="bg-purple-50 border-purple-200">
        <CardContent className="pt-4">
          <div className="flex gap-3">
            <div className="text-purple-600">📋</div>
            <div className="text-sm text-purple-800">
              <p className="font-medium mb-1">How task types work:</p>
              <ul className="list-disc list-inside space-y-1 text-purple-700">
                <li>Tasks appear in the inspection form under &quot;Assign Tasks&quot;</li>
                <li>When users suggest new tasks, they appear here for approval</li>
                <li>Approved tasks become available to all users</li>
                <li>Default tasks cannot be deleted, only deactivated</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Action Types Configuration Panel
const ActionTypesPanel = ({ token, loading: parentLoading }) => {
  const [actionTypes, setActionTypes] = useState([]);
  const [pendingActions, setPendingActions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newActionName, setNewActionName] = useState('');
  const [newActionCategory, setNewActionCategory] = useState('other');

  useEffect(() => {
    fetchActionTypes();
  }, []);

  const fetchActionTypes = async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API_URL}/api/action-types?include_pending=true`, { headers });
      const data = await res.json();
      setActionTypes(data.items || []);
      setPendingActions(data.pending || []);
      setCategories(data.categories || []);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleAddAction = async () => {
    if (!newActionName.trim()) {
      toast.error('Please enter an action name');
      return;
    }
    
    setSaving(true);
    try {
      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      const res = await fetch(`${API_URL}/api/action-types`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ name: newActionName.trim(), category: newActionCategory })
      });
      const data = await res.json();
      toast.success(data.message || 'Action added!');
      setNewActionName('');
      setNewActionCategory('other');
      fetchActionTypes();
    } catch (err) {
      toast.error('Failed to add action');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (actionId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await fetch(`${API_URL}/api/action-types/${actionId}/approve`, {
        method: 'POST',
        headers
      });
      toast.success('Action approved!');
      fetchActionTypes();
    } catch (err) {
      toast.error('Failed to approve');
    }
  };

  const handleReject = async (actionId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await fetch(`${API_URL}/api/action-types/${actionId}/reject`, {
        method: 'POST',
        headers
      });
      toast.success('Action rejected');
      fetchActionTypes();
    } catch (err) {
      toast.error('Failed to reject');
    }
  };

  const handleToggleActive = async (actionId, isActive) => {
    try {
      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      const response = await fetch(`${API_URL}/api/action-types/${actionId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ is_active: !isActive })
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update');
      }
      fetchActionTypes();
    } catch (err) {
      toast.error(err.message || 'Failed to update');
    }
  };

  const handleDelete = async (actionId) => {
    if (!confirm('Delete this action type?')) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await fetch(`${API_URL}/api/action-types/${actionId}`, {
        method: 'DELETE',
        headers
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to delete action');
      }
      toast.success('Action deleted');
      fetchActionTypes();
    } catch (err) {
      toast.error(err.message || 'Cannot delete this action');
    }
  };

  const getCategoryColor = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    const colors = {
      amber: 'bg-amber-100 text-amber-800',
      blue: 'bg-blue-100 text-blue-800',
      purple: 'bg-purple-100 text-purple-800',
      yellow: 'bg-yellow-100 text-yellow-800',
      green: 'bg-green-100 text-green-800',
      stone: 'bg-stone-100 text-stone-800'
    };
    return colors[cat?.color] || colors.stone;
  };

  if (loading || parentLoading) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center text-gray-500">Loading action types...</div>
        </CardContent>
      </Card>
    );
  }

  // Group actions by category
  const actionsByCategory = {};
  actionTypes.forEach(action => {
    const cat = action.category || 'other';
    if (!actionsByCategory[cat]) actionsByCategory[cat] = [];
    actionsByCategory[cat].push(action);
  });

  return (
    <div className="space-y-6">
      {/* Pending Approval Section */}
      {pendingActions.length > 0 && (
        <Card className="border-2 border-amber-400 bg-amber-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-800">
              <Clock className="w-5 h-5" />
              Pending Approval ({pendingActions.length})
            </CardTitle>
            <CardDescription className="text-amber-700">
              User-submitted actions waiting for your approval
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pendingActions.map(action => (
                <div key={action.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200">
                  <div>
                    <span className="font-medium">{action.name}</span>
                    <span className="text-xs text-gray-500 ml-2">by {action.submitted_by_name}</span>
                    <Badge className={`ml-2 ${getCategoryColor(action.category)}`}>
                      {categories.find(c => c.id === action.category)?.name || action.category}
                    </Badge>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleApprove(action.id)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleReject(action.id)}
                      className="text-red-600 border-red-300 hover:bg-red-50"
                    >
                      <X className="w-4 h-4 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add New Action */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-600" />
            Action Types
          </CardTitle>
          <CardDescription>
            Configure available actions that can be recorded during inspections
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3 mb-6">
            <Input
              value={newActionName}
              onChange={(e) => setNewActionName(e.target.value)}
              placeholder="New action name..."
              className="flex-1"
            />
            <Select value={newActionCategory} onValueChange={setNewActionCategory}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={handleAddAction} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
              <Plus className="w-4 h-4 mr-1" /> Add Action
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Action List by Category */}
      {categories.map(category => {
        const actions = actionsByCategory[category.id] || [];
        if (actions.length === 0) return null;
        
        return (
          <Card key={category.id}>
            <CardHeader className="py-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Badge className={getCategoryColor(category.id)}>{category.name}</Badge>
                <span className="text-sm text-gray-500">({actions.length} actions)</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {actions.map(action => (
                  <div 
                    key={action.id} 
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      action.is_active === false ? 'bg-gray-50 opacity-60' : 'bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={action.is_active === false ? 'line-through text-gray-400' : ''}>
                        {action.name}
                      </span>
                      {action.is_default && (
                        <Badge variant="outline" className="text-xs">Default</Badge>
                      )}
                      {action.has_qty && (
                        <Badge variant="outline" className="text-xs bg-blue-50">Has Qty</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={action.is_active !== false}
                        onCheckedChange={() => handleToggleActive(action.id, action.is_active !== false)}
                      />
                      {!action.is_default && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(action.id)}
                          className="text-red-500 hover:text-red-700 h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Info Card */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="pt-4">
          <div className="flex gap-3">
            <div className="text-amber-600">⚡</div>
            <div className="text-sm text-amber-800">
              <p className="font-medium mb-1">How action types work:</p>
              <ul className="list-disc list-inside space-y-1 text-amber-700">
                <li>Actions appear in the &quot;Actions Taken&quot; section of inspection forms</li>
                <li>Special actions may have additional detail fields</li>
                <li>Equipment actions can track quantities added/removed</li>
                <li>Default actions cannot be deleted, only deactivated</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Demo Videos Configuration Panel
const DemoVideosPanel = ({ token, loading: parentLoading }) => {
  const [videos, setVideos] = useState([]);
  const [activeVideoId, setActiveVideoId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [mp4File, setMp4File] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    video_url: '',
    thumbnail_url: '',
    duration: ''
  });

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API_URL}/api/demo-videos`, { headers });
      const data = await res.json();
      setVideos(data.videos || []);
      setActiveVideoId(data.active_video_id);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    if (!formData.video_url.trim() && !mp4File) {
      toast.error('Please enter a video URL or upload an MP4 file');
      return;
    }
    
    setSaving(true);
    try {
      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      let videoId = editingVideo?.id;
      
      if (editingVideo) {
        await fetch(`${API_URL}/api/demo-videos/${editingVideo.id}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(formData)
        });
      } else {
        const res = await fetch(`${API_URL}/api/demo-videos`, {
          method: 'POST',
          headers,
          body: JSON.stringify(formData)
        });
        const created = await res.json();
        videoId = created.id;
      }
      
      // Upload MP4 file if selected
      if (mp4File && videoId) {
        setUploading(true);
        setUploadProgress(10);
        const uploadForm = new FormData();
        uploadForm.append('file', mp4File);
        setUploadProgress(30);
        
        const uploadRes = await fetch(`${API_URL}/api/demo-videos/${videoId}/upload-mp4`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: uploadForm
        });
        setUploadProgress(90);
        
        if (!uploadRes.ok) {
          const err = await uploadRes.json().catch(() => ({}));
          throw new Error(err.detail || 'Upload failed');
        }
        setUploadProgress(100);
        setUploading(false);
      }
      
      toast.success(editingVideo ? 'Video updated!' : 'Video added!');
      setFormData({ title: '', description: '', video_url: '', thumbnail_url: '', duration: '' });
      setMp4File(null);
      setShowAddForm(false);
      setEditingVideo(null);
      fetchVideos();
    } catch (err) {
      toast.error(err.message || 'Failed to save video');
      setUploading(false);
    } finally {
      setSaving(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (videoId) => {
    if (!confirm('Delete this video?')) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await fetch(`${API_URL}/api/demo-videos/${videoId}`, {
        method: 'DELETE',
        headers
      });
      toast.success('Video deleted');
      fetchVideos();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleSetActive = async (videoId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await fetch(`${API_URL}/api/demo-videos/${videoId}/set-active`, {
        method: 'POST',
        headers
      });
      setActiveVideoId(videoId);
      toast.success('Video set as active!');
    } catch (err) {
      toast.error('Failed to set active');
    }
  };

  const handleClearActive = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await fetch(`${API_URL}/api/demo-videos/clear-active`, {
        method: 'POST',
        headers
      });
      setActiveVideoId(null);
      toast.success('Active video cleared');
    } catch (err) {
      toast.error('Failed to clear');
    }
  };

  const startEdit = (video) => {
    setEditingVideo(video);
    setMp4File(null);
    setFormData({
      title: video.title || '',
      description: video.description || '',
      video_url: video.video_url || '',
      thumbnail_url: video.thumbnail_url || '',
      duration: video.duration || ''
    });
    setShowAddForm(true);
  };

  // Extract YouTube video ID for thumbnail
  const getYouTubeThumbnail = (url) => {
    const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([^&?/]+)/);
    return match ? `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg` : null;
  };

  if (loading || parentLoading) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center text-gray-500">Loading demo videos...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Add/Edit Form */}
      {showAddForm && (
        <Card className="border-2 border-blue-300 bg-blue-50">
          <CardHeader>
            <CardTitle className="text-blue-800">
              {editingVideo ? '✏️ Edit Video' : '➕ Add Demo Video'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Getting Started with Thrive"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Duration</Label>
                <Input
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  placeholder="e.g., 1:30"
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium">Video URL (YouTube, Vimeo, or direct link)</Label>
              <Input
                value={formData.video_url}
                onChange={(e) => { setFormData({ ...formData, video_url: e.target.value }); setMp4File(null); }}
                placeholder="https://www.youtube.com/watch?v=..."
                className="mt-1"
                disabled={!!mp4File}
                data-testid="demo-video-url-input"
              />
            </div>
            <div className="relative">
              <div className="flex items-center gap-3 my-1">
                <div className="flex-1 border-t border-stone-300" />
                <span className="text-xs text-stone-400 font-medium">OR</span>
                <div className="flex-1 border-t border-stone-300" />
              </div>
              <Label className="text-sm font-medium">Upload MP4 Video (max 100MB)</Label>
              <div className="mt-1">
                {mp4File ? (
                  <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <Video className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-green-800 truncate">{mp4File.name}</p>
                      <p className="text-xs text-green-600">{(mp4File.size / 1024 / 1024).toFixed(1)} MB</p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setMp4File(null)}
                      className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                      data-testid="remove-mp4-btn"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-stone-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors" data-testid="mp4-upload-area">
                    <Upload className="w-5 h-5 text-stone-400" />
                    <span className="text-sm text-stone-500">Click to select MP4 file</span>
                    <input
                      type="file"
                      accept="video/mp4,video/quicktime,video/x-msvideo,.mp4,.mov,.avi"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) {
                          if (f.size > 100 * 1024 * 1024) {
                            toast.error('File too large (max 100MB)');
                            return;
                          }
                          setMp4File(f);
                          setFormData(prev => ({ ...prev, video_url: '' }));
                        }
                      }}
                      data-testid="mp4-file-input"
                    />
                  </label>
                )}
                {uploading && (
                  <div className="mt-2">
                    <div className="w-full bg-stone-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <p className="text-xs text-blue-600 mt-1">Uploading video... {uploadProgress}%</p>
                  </div>
                )}
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium">Thumbnail URL (optional - auto-generated for YouTube)</Label>
              <Input
                value={formData.thumbnail_url}
                onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
                placeholder="https://..."
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of the video"
                className="mt-1"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={handleSave} disabled={saving || uploading} className="bg-blue-600 hover:bg-blue-700" data-testid="save-demo-video-btn">
                {uploading ? 'Uploading...' : saving ? 'Saving...' : editingVideo ? 'Update Video' : 'Add Video'}
              </Button>
              <Button variant="outline" onClick={() => { setShowAddForm(false); setEditingVideo(null); setMp4File(null); }}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Panel */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Video className="w-5 h-5 text-blue-600" />
                Demo Videos
              </CardTitle>
              <CardDescription>
                Manage demo videos shown on the login page
              </CardDescription>
            </div>
            {!showAddForm && (
              <Button onClick={() => setShowAddForm(true)} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-1" /> Add Video
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {videos.length === 0 ? (
            <div className="text-center py-8 text-stone-500">
              <Video className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No demo videos yet</p>
              <p className="text-sm">Add your first video to display on the login page</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {videos.map((video) => {
                const thumbnail = video.thumbnail_url || getYouTubeThumbnail(video.video_url);
                const isActive = video.id === activeVideoId;
                
                return (
                  <div 
                    key={video.id} 
                    className={`relative rounded-xl overflow-hidden border-2 transition-all ${
                      isActive ? 'border-green-500 ring-2 ring-green-200' : 'border-stone-200 hover:border-blue-300'
                    }`}
                  >
                    {/* Thumbnail */}
                    <div className="relative aspect-video bg-stone-200">
                      {thumbnail ? (
                        <img src={thumbnail} alt={video.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-stone-100">
                          <Video className="w-12 h-12 text-stone-300" />
                        </div>
                      )}
                      {/* Play overlay */}
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                        <div className="bg-white/90 rounded-full p-3">
                          <Play className="w-8 h-8 text-blue-600 fill-current" />
                        </div>
                      </div>
                      {/* Active badge */}
                      {isActive && (
                        <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> ACTIVE
                        </div>
                      )}
                      {/* Duration */}
                      {video.duration && (
                        <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-0.5 rounded text-xs">
                          {video.duration}
                        </div>
                      )}
                      {/* Video type badge */}
                      {video.video_type === 'mp4' && (
                        <div className="absolute top-2 right-2 bg-blue-600 text-white px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1">
                          <Upload className="w-3 h-3" /> MP4
                        </div>
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="p-3">
                      <h4 className="font-semibold text-stone-800 truncate">{video.title}</h4>
                      {video.description && (
                        <p className="text-sm text-stone-500 truncate">{video.description}</p>
                      )}
                      
                      {/* Actions */}
                      <div className="flex items-center justify-between mt-3 pt-3 border-t">
                        {isActive ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleClearActive}
                            className="text-orange-600 border-orange-300 hover:bg-orange-50"
                          >
                            Remove Active
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSetActive(video.id)}
                            className="text-green-600 border-green-300 hover:bg-green-50"
                          >
                            Set as Active
                          </Button>
                        )}
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => startEdit(video)}
                            className="h-8 w-8 p-0 text-blue-600"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(video.id)}
                            className="h-8 w-8 p-0 text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4">
          <div className="flex gap-3">
            <div className="text-blue-600">🎬</div>
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">How demo videos work:</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li>The &quot;Active&quot; video displays on the login/signup page</li>
                <li>YouTube URLs automatically generate thumbnails</li>
                <li>You can upload MP4 files directly (up to 100MB)</li>
                <li>You can add multiple videos and switch between them</li>
                <li>Videos play in a modal when users click the thumbnail</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Month names for dropdown
const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];


const MONTHLY_TEMPLATES = [
  { month: 1, title: "New Beginnings", description: "First DIY project of the year — any category welcome!" },
  { month: 2, title: "Budget Builds", description: "Best project under $50." },
  { month: 3, title: "Recycle & Repurpose", description: "Builds using recycled or salvaged materials." },
  { month: 4, title: "Beginner Friendly", description: "Easiest builds any new beekeeper can tackle." },
  { month: 5, title: "Tool Time", description: "Best jigs, tools, and workshop setups." },
  { month: 6, title: "Feed the Bees", description: "Feeders, nutrition stations, and planting guides." },
  { month: 7, title: "Hive Innovation", description: "Most creative hive modification or custom build." },
  { month: 8, title: "Protect & Prevent", description: "Pest management, ventilation, weatherproofing." },
  { month: 9, title: "Extraction Station", description: "Honey processing and bottling setups." },
  { month: 10, title: "Community Choice", description: "The community voted — this month's theme is chosen by you!" },
  { month: 11, title: "Workshop Warriors", description: "One-day builds: must be completable in a single day." },
  { month: 12, title: "Best of the Year", description: "Community votes on the top builds from all 12 months." },
];

const MONTH_NAMES = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const ChallengeManagerPanel = ({ token }) => {
  const [schedule, setSchedule] = useState([...MONTHLY_TEMPLATES]);
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bonusTitle, setBonusTitle] = useState('');
  const [bonusDesc, setBonusDesc] = useState('');
  const [creating, setCreating] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  useEffect(() => {
    const fetchChallenges = async () => {
      try {
        const res = await fetch(`${API_URL}/api/diy-challenges`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) setChallenges(await res.json());
      } catch {}
      setLoading(false);
    };
    fetchChallenges();
  }, [token]);

  const launchMonthly = async (tmpl) => {
    setCreating(true);
    try {
      const now = new Date();
      const res = await fetch(`${API_URL}/api/diy-challenges`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: tmpl.title, description: tmpl.description, month: now.getMonth() + 1, year: now.getFullYear(), challenge_type: 'monthly' }),
      });
      if (res.ok) {
        const ch = await res.json();
        setChallenges(prev => [ch, ...prev]);
        toast.success(`"${tmpl.title}" challenge launched!`);
      }
    } catch {}
    setCreating(false);
  };

  const launchBonus = async () => {
    if (!bonusTitle.trim()) return;
    setCreating(true);
    try {
      const now = new Date();
      const res = await fetch(`${API_URL}/api/diy-challenges`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: bonusTitle.trim(), description: bonusDesc.trim(), month: now.getMonth() + 1, year: now.getFullYear(), challenge_type: 'bonus' }),
      });
      if (res.ok) {
        const ch = await res.json();
        setChallenges(prev => [ch, ...prev]);
        setBonusTitle(''); setBonusDesc('');
        toast.success('Bonus challenge launched!');
      }
    } catch {}
    setCreating(false);
  };

  const closeChallenge = async (chId, winnerEntryId) => {
    try {
      await fetch(`${API_URL}/api/diy-challenges/${chId}/close`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ winner_entry_id: winnerEntryId || null }),
      });
      setChallenges(prev => prev.map(c => c.id === chId ? { ...c, status: 'closed' } : c));
      toast.success('Challenge closed');
    } catch {}
  };

  const moveTemplate = (from, to) => {
    if (to < 0 || to >= schedule.length) return;
    const updated = [...schedule];
    const [item] = updated.splice(from, 1);
    updated.splice(to, 0, item);
    updated.forEach((t, i) => t.month = i + 1);
    setSchedule(updated);
  };

  if (loading) return <div className="text-center py-8 text-stone-500">Loading...</div>;

  const activeChallenges = challenges.filter(c => c.status === 'active');
  const pastChallenges = challenges.filter(c => c.status !== 'active');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Zap className="w-5 h-5 text-orange-600" /> Challenge Manager</CardTitle>
        <CardDescription>Schedule monthly challenges and launch bonus events.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Active Challenges */}
        {activeChallenges.length > 0 && (
          <div>
            <h3 className="text-sm font-bold text-stone-800 mb-2">Active Challenges</h3>
            {activeChallenges.map(ch => (
              <div key={ch.id} className="bg-green-50 border border-green-200 rounded-xl p-3 mb-2 flex items-center justify-between">
                <div>
                  <span className="font-semibold text-green-800 text-sm">{ch.title}</span>
                  {ch.challenge_type === 'bonus' && <span className="ml-2 text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">Bonus</span>}
                  <span className="text-xs text-green-600 ml-2">{ch.entry_count || 0} entries</span>
                </div>
                <button onClick={() => closeChallenge(ch.id)} className="text-xs bg-red-100 text-red-700 px-3 py-1 rounded-full font-medium hover:bg-red-200">Close</button>
              </div>
            ))}
          </div>
        )}

        {/* Monthly Template Schedule */}
        <div>
          <h3 className="text-sm font-bold text-stone-800 mb-2">Monthly Schedule (drag to reorder)</h3>
          <div className="space-y-1">
            {schedule.map((tmpl, idx) => (
              <div key={tmpl.title}
                draggable
                onDragStart={() => setDragIdx(idx)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { if (dragIdx !== null) moveTemplate(dragIdx, idx); setDragIdx(null); }}
                className="flex items-center gap-3 bg-stone-50 rounded-lg px-3 py-2 border border-stone-200 cursor-grab active:cursor-grabbing hover:bg-stone-100 transition"
              >
                <span className="text-xs font-bold text-stone-500 w-8">{MONTH_NAMES[tmpl.month]}</span>
                <div className="flex-1">
                  <span className="text-sm font-medium text-stone-800">{tmpl.title}</span>
                  <span className="text-xs text-stone-400 ml-2">{tmpl.description.slice(0, 50)}</span>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => moveTemplate(idx, idx - 1)} disabled={idx === 0} className="text-stone-400 hover:text-stone-600 disabled:opacity-30 text-xs">▲</button>
                  <button onClick={() => moveTemplate(idx, idx + 1)} disabled={idx === schedule.length - 1} className="text-stone-400 hover:text-stone-600 disabled:opacity-30 text-xs">▼</button>
                </div>
                <button onClick={() => launchMonthly(tmpl)} disabled={creating} className="text-xs bg-amber-600 text-white px-3 py-1 rounded-full font-medium hover:bg-amber-700 disabled:opacity-50">Launch</button>
              </div>
            ))}
          </div>
        </div>

        {/* Bonus Challenge */}
        <div className="pt-4 border-t border-stone-200">
          <h3 className="text-sm font-bold text-stone-800 mb-2">Launch Bonus Challenge</h3>
          <div className="flex gap-2 items-end">
            <div className="flex-1 space-y-2">
              <input value={bonusTitle} onChange={e => setBonusTitle(e.target.value)} placeholder="e.g., Workshop Warriors" className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm" />
              <input value={bonusDesc} onChange={e => setBonusDesc(e.target.value)} placeholder="Description..." className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <button onClick={launchBonus} disabled={creating || !bonusTitle.trim()} className="bg-purple-600 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-purple-700 disabled:opacity-50 flex-shrink-0">Launch Bonus</button>
          </div>
        </div>

        {/* Past Challenges */}
        {pastChallenges.length > 0 && (
          <div className="pt-4 border-t border-stone-200">
            <h3 className="text-sm font-bold text-stone-800 mb-2">Past Challenges ({pastChallenges.length})</h3>
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {pastChallenges.map(ch => (
                <div key={ch.id} className="flex items-center justify-between bg-stone-50 rounded-lg px-3 py-2 text-sm">
                  <div>
                    <span className="font-medium text-stone-700">{ch.title}</span>
                    {ch.winner_name && <span className="text-xs text-amber-600 ml-2">Winner: {ch.winner_name}</span>}
                  </div>
                  <span className="text-xs text-stone-400">{ch.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};


const BeekeeperTasksPanel = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/seasonal-tasks/all`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) setData(await res.json());
      } catch { /* empty */ }
      setLoading(false);
    };
    fetchTasks();
  }, [token]);

  if (loading) return <div className="text-center py-8 text-stone-500">Loading...</div>;
  if (!data || data.total === 0) return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><ClipboardList className="w-5 h-5 text-amber-600" /> Beekeeper Custom Tasks</CardTitle>
        <CardDescription>See what tasks your beekeepers are adding to their seasonal checklists. Great for identifying feature opportunities.</CardDescription>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-stone-500 italic">No custom tasks have been created by beekeepers yet. These appear when beekeepers on 30+ hive plans add their own seasonal tasks.</p>
      </CardContent>
    </Card>
  );

  const SEASON_COLORS = { spring: 'bg-green-50 border-green-200 text-green-800', summer: 'bg-amber-50 border-amber-200 text-amber-800', autumn: 'bg-orange-50 border-orange-200 text-orange-800', winter: 'bg-blue-50 border-blue-200 text-blue-800' };
  const SEASON_LABELS = { spring: 'Spring', summer: 'Summer', autumn: 'Autumn', winter: 'Winter' };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><ClipboardList className="w-5 h-5 text-amber-600" /> Beekeeper Custom Tasks</CardTitle>
        <CardDescription>
          {data.total} custom task{data.total !== 1 ? 's' : ''} created by beekeepers. Use these insights to build features beekeepers actually need.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {['spring', 'summer', 'autumn', 'winter'].map(season => {
          const tasks = data.by_season[season] || [];
          if (tasks.length === 0) return null;
          return (
            <div key={season}>
              <h4 className={`text-sm font-semibold px-3 py-1.5 rounded-lg border mb-2 ${SEASON_COLORS[season]}`}>
                {SEASON_LABELS[season]} ({tasks.length})
              </h4>
              <div className="space-y-1.5">
                {tasks.map(t => (
                  <div key={t.id} className="flex items-center justify-between bg-stone-50 rounded-lg px-3 py-2 text-sm">
                    <div className="flex-1">
                      <span className="font-medium text-stone-700">{t.task}</span>
                      <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-full border font-medium ${
                        t.priority === 'high' ? 'text-red-600 bg-red-50 border-red-200' :
                        t.priority === 'low' ? 'text-stone-500 bg-stone-100 border-stone-200' :
                        'text-amber-600 bg-amber-50 border-amber-200'
                      }`}>{t.priority}</span>
                    </div>
                    <div className="text-xs text-stone-400 text-right ml-3">
                      <div>{t.user_name || t.user_email}</div>
                      {t.company_name && <div className="text-stone-300">{t.company_name}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default function AdminConfiguration() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('hive_types');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Harvest config state
  const [harvestConfig, setHarvestConfig] = useState(null);
  const [harvestSaving, setHarvestSaving] = useState(false);
  
  // Pending counts for alert badges
  const [pendingCounts, setPendingCounts] = useState({ pending_tasks: 0, pending_actions: 0 });

  const token = localStorage.getItem('token');
  
  // Fetch pending counts on mount
  useEffect(() => {
    const fetchPendingCounts = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await fetch(`${API_URL}/api/admin/pending-counts`, { headers });
        if (res.ok) {
          const data = await res.json();
          setPendingCounts(data);
        }
      } catch (err) {
      }
    };
    fetchPendingCounts();
  }, [token, activeTab]); // Refresh when tab changes

  const fetchItems = async (configType) => {
    // Handle tabs that have their own internal data fetching
    if (['task_types', 'action_types', 'product_types', 'demo_videos', 'beekeeper_tasks', 'challenges'].includes(configType)) {
      setLoading(false);
      setError('');
      return;
    }
    
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/admin/config/${configType}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch configuration');
      const data = await response.json();
      setItems(data.items || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Default harvesting equipment for seeding
  const defaultHarvestingEquipment = [
    { id: 'extractors', name: 'Honey Extractors', key: 'extractors', qty: 0, price: 0 },
    { id: 'uncapping_knives', name: 'Uncapping Knives', key: 'uncapping_knives', qty: 0, price: 0 },
    { id: 'uncapping_forks', name: 'Uncapping Forks', key: 'uncapping_forks', qty: 0, price: 0 },
    { id: 'uncapping_tanks', name: 'Uncapping Tanks', key: 'uncapping_tanks', qty: 0, price: 0 },
    { id: 'honey_buckets', name: 'Honey Buckets', key: 'honey_buckets', qty: 0, price: 0 },
    { id: 'strainers', name: 'Strainers/Filters', key: 'strainers', qty: 0, price: 0 },
    { id: 'bottling_tanks', name: 'Bottling Tanks', key: 'bottling_tanks', qty: 0, price: 0 },
    { id: 'bottling_equipment', name: 'Bottling Equipment', key: 'bottling_equipment', qty: 0, price: 0 },
    { id: 'honey_gates', name: 'Honey Gates', key: 'honey_gates', qty: 0, price: 0 },
    { id: 'refractometers', name: 'Refractometers', key: 'refractometers', qty: 0, price: 0 },
    { id: 'bee_escapes', name: 'Bee Escapes', key: 'bee_escapes', qty: 0, price: 0 },
    { id: 'fume_boards', name: 'Fume Boards', key: 'fume_boards', qty: 0, price: 0 },
    { id: 'capping_scratchers', name: 'Capping Scratchers', key: 'capping_scratchers', qty: 0, price: 0 },
    { id: 'honey_warmers', name: 'Honey Warmers', key: 'honey_warmers', qty: 0, price: 0 },
    { id: 'wax_melters', name: 'Wax Melters', key: 'wax_melters', qty: 0, price: 0 },
  ];

  const fetchHarvestConfig = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/harvest-config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch harvest configuration');
      const data = await response.json();
      
      // Auto-seed defaults if harvesting_equipment is missing
      if (!data.harvesting_equipment || data.harvesting_equipment.length === 0) {
        const seededConfig = { ...data, harvesting_equipment: defaultHarvestingEquipment };
        // Save the seeded config to backend
        await fetch(`${API_URL}/api/harvest-config`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(seededConfig)
        });
        setHarvestConfig(seededConfig);
      } else {
        setHarvestConfig(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const saveHarvestConfig = async () => {
    setHarvestSaving(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/harvest-config`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(harvestConfig)
      });
      if (!response.ok) throw new Error('Failed to save harvest configuration');
      const data = await response.json();
      setHarvestConfig(data);
      toast.success('Harvest configuration saved!');
    } catch (err) {
      setError(err.message);
      toast.error('Failed to save configuration');
    } finally {
      setHarvestSaving(false);
    }
  };

  useEffect(() => {
    fetchItems(activeTab);
  }, [activeTab]);

  const handleTabChange = (value) => {
    setActiveTab(value);
    setEditingItem(null);
    setIsAdding(false);
    setFormData({});
  };

  const handleAdd = () => {
    setIsAdding(true);
    setEditingItem(null);
    setFormData({});
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setIsAdding(false);
    // Convert arrays back to comma-separated strings for editing
    const editData = { ...item };
    FIELD_CONFIGS[activeTab]?.forEach(field => {
      if (field.isArray && Array.isArray(editData[field.key])) {
        editData[field.key] = editData[field.key].join(', ');
      }
    });
    setFormData(editData);
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingItem(null);
    setFormData({});
  };

  const handleInputChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    
    try {
      // Process array fields
      const processedData = { ...formData };
      FIELD_CONFIGS[activeTab]?.forEach(field => {
        if (field.isArray && typeof processedData[field.key] === 'string') {
          processedData[field.key] = processedData[field.key]
            .split(',')
            .map(s => s.trim())
            .filter(s => s);
        }
      });

      const url = editingItem 
        ? `${API_URL}/api/admin/config/${activeTab}/${editingItem.id}`
        : `${API_URL}/api/admin/config/${activeTab}`;
      
      const method = editingItem ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(processedData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save');
      }

      await fetchItems(activeTab);
      handleCancel();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/config/${activeTab}/${itemId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete');
      }

      await fetchItems(activeTab);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggleActive = async (itemId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/config/${activeTab}/${itemId}/toggle`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to toggle status');
      await fetchItems(activeTab);
    } catch (err) {
      setError(err.message);
    }
  };

  const renderField = (field) => {
    const value = formData[field.key] ?? '';
    
    switch (field.type) {
      case 'textarea':
        return (
          <Textarea
            value={value}
            onChange={(e) => handleInputChange(field.key, e.target.value)}
            placeholder={field.label}
            rows={3}
          />
        );
      case 'select':
        // Helper to format section names nicely
        const formatOption = (opt) => {
          const sectionLabels = {
            // Tool sections
            'protective_gear': '🧤 Protective Gear',
            'hive_tools': '🔧 Hive Tools',
            'feeding_equipment': '🍯 Feeding Equipment',
            'harvesting_equipment': '🔶 Harvesting Equipment',
            'heavy_equipment': '📦 Heavy Equipment',
            'miscellaneous': '📋 Miscellaneous',
            // Component sections
            'lids_covers': '🏠 Lids & Covers',
            'feeders': '🍯 Feeders',
            'queen_management': '👑 Queen Management',
            'bottom_boards': '📋 Bottom Boards & Stands',
            'pest_control': '🐛 Pest Control',
            'heavy_accessories': '⚙️ Heavy & Accessories'
          };
          return sectionLabels[opt] || opt || '(none)';
        };
        
        return (
          <Select value={value || '_none_'} onValueChange={(v) => handleInputChange(field.key, v === '_none_' ? '' : v)}>
            <SelectTrigger>
              <SelectValue placeholder={`Select ${field.label}`} />
            </SelectTrigger>
            <SelectContent>
              {field.options.map(opt => (
                <SelectItem key={opt || '_none_'} value={opt || '_none_'}>
                  {field.key === 'section' ? formatOption(opt) : (opt || '(none)')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case 'number':
        return (
          <Input
            type="number"
            value={value}
            onChange={(e) => handleInputChange(field.key, parseFloat(e.target.value) || 0)}
            placeholder={field.placeholder || field.label}
          />
        );
      case 'boolean':
        return (
          <div className="flex flex-col gap-1">
            <div className="flex items-center space-x-2">
              <Switch
                checked={!!value}
                onCheckedChange={(v) => handleInputChange(field.key, v)}
              />
              <span className="text-sm text-gray-600">{value ? 'Yes' : 'No'}</span>
            </div>
            {field.helpText && (
              <span className="text-xs text-gray-500">{field.helpText}</span>
            )}
          </div>
        );
      case 'box_config':
        // Only show if requires_boxes is true
        if (!formData.requires_boxes) return null;
        
        const boxTypes = Array.isArray(value) ? value : [];
        
        const addBoxType = () => {
          const newBox = { name: '', sizes: ['8', '9', '10'], max_qty: 4 };
          handleInputChange(field.key, [...boxTypes, newBox]);
        };
        
        const updateBoxType = (index, updates) => {
          const updated = [...boxTypes];
          updated[index] = { ...updated[index], ...updates };
          handleInputChange(field.key, updated);
        };
        
        const removeBoxType = (index) => {
          handleInputChange(field.key, boxTypes.filter((_, i) => i !== index));
        };
        
        const addPresetBox = (preset) => {
          handleInputChange(field.key, [...boxTypes, { ...preset }]);
        };
        
        return (
          <div className="space-y-3">
            <div className="text-sm text-gray-600 mb-2">
              Define the types of boxes available for this hive type
            </div>
            
            {/* Quick add presets */}
            <div className="flex flex-wrap gap-2 mb-3">
              <span className="text-xs text-gray-500 self-center">Quick add:</span>
              {DEFAULT_BOX_OPTIONS.filter(preset => !boxTypes.some(b => b.name === preset.name)).slice(0, 4).map(preset => (
                <Button
                  key={preset.name}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => addPresetBox(preset)}
                  className="text-xs h-7"
                >
                  + {preset.name}
                </Button>
              ))}
            </div>
            
            {/* Existing box types */}
            {boxTypes.map((box, index) => (
              <div key={index} className="p-3 bg-gray-50 border rounded-lg space-y-2">
                <div className="flex items-center gap-2">
                  <Input
                    value={box.name}
                    onChange={(e) => updateBoxType(index, { name: e.target.value })}
                    placeholder="Box name (e.g., Deep Brood)"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeBoxType(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Frame Sizes (comma sep.)</Label>
                    <Input
                      value={box.sizesInput !== undefined ? box.sizesInput : (Array.isArray(box.sizes) ? box.sizes.join(', ') : '')}
                      onChange={(e) => updateBoxType(index, { sizesInput: e.target.value })}
                      onBlur={(e) => {
                        const sizes = e.target.value.split(',').map(s => s.trim()).filter(s => s);
                        updateBoxType(index, { sizes, sizesInput: undefined });
                      }}
                      placeholder="8, 9, 10"
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Max Quantity</Label>
                    <Input
                      type="number"
                      min="1"
                      max="10"
                      value={box.max_qty || 4}
                      onChange={(e) => updateBoxType(index, { max_qty: parseInt(e.target.value) || 4 })}
                      className="text-sm"
                    />
                  </div>
                </div>
              </div>
            ))}
            
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addBoxType}
              className="w-full"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Custom Box Type
            </Button>
          </div>
        );
      case 'toggle_group':
        const selectedItems = Array.isArray(value) ? value : (typeof value === 'string' ? value.split(',').map(s => s.trim()).filter(Boolean) : []);
        return (
          <div className="flex flex-wrap gap-1.5">
            {(field.options || []).map(opt => {
              const isSelected = selectedItems.includes(opt);
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => {
                    const updated = isSelected ? selectedItems.filter(s => s !== opt) : [...selectedItems, opt];
                    handleInputChange(field.key, updated);
                  }}
                  className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors ${
                    isSelected
                      ? 'bg-amber-600 text-white border-amber-600'
                      : 'bg-white text-stone-600 border-stone-300 hover:border-amber-300 hover:bg-amber-50'
                  }`}
                >
                  {opt}
                </button>
              );
            })}
          </div>
        );
      default:
        // Handle isArray fields - convert array to comma-separated string for display
        let displayValue = value;
        if (field.isArray && Array.isArray(value)) {
          displayValue = value.join(', ');
        }
        return (
          <Input
            type="text"
            value={displayValue || ''}
            onChange={(e) => handleInputChange(field.key, e.target.value)}
            placeholder={field.placeholder || field.label}
          />
        );
    }
  };

  const renderItemCard = (item) => {
    const isInactive = item.is_active === false;
    
    return (
      <div
        key={item.id}
        className={`p-4 border rounded-lg ${isInactive ? 'bg-gray-100 opacity-60' : 'bg-white'} hover:shadow-sm transition-shadow`}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <GripVertical className="w-5 h-5 text-gray-400 mt-1 cursor-move" />
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-medium">{item.name}</h3>
                {item.is_default && (
                  <Badge variant="secondary" className="text-xs">Default</Badge>
                )}
                {isInactive && (
                  <Badge variant="outline" className="text-xs text-gray-500">Inactive</Badge>
                )}
              </div>
              {item.description && (
                <p className="text-sm text-gray-500 mt-1">{item.description}</p>
              )}
              {item.category && (
                <span className="inline-block text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded mt-1">
                  {item.category}
                </span>
              )}
              {/* Show severity badge for critical alerts */}
              {activeTab === 'critical_alerts' && item.severity && (
                <span className={`inline-block text-xs px-2 py-0.5 rounded ml-2 ${
                  item.severity === 'critical' ? 'bg-red-100 text-red-700' :
                  item.severity === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-blue-100 text-blue-700'
                }`}>
                  {item.severity}
                </span>
              )}
              {/* Show trigger values for critical alerts */}
              {activeTab === 'critical_alerts' && item.trigger_values && item.trigger_values.length > 0 && (
                <div className="mt-1">
                  <span className="text-xs text-gray-500">Triggers: </span>
                  <span className="text-xs text-gray-600">{item.trigger_values.join(', ')}</span>
                </div>
              )}
              {/* Show enabled status for critical alerts */}
              {activeTab === 'critical_alerts' && (
                <div className="mt-1 flex items-center gap-2">
                  <span className={`text-xs font-medium ${item.is_enabled ? 'text-green-600' : 'text-gray-400'}`}>
                    {item.is_enabled ? '✓ Enabled' : '○ Disabled'}
                  </span>
                </div>
              )}
              {item.default_price > 0 && (
                <span className="inline-block text-xs text-green-600 ml-2">
                  ${item.default_price}
                </span>
              )}
              {/* Show frame types for hive types */}
              {item.frame_types && item.frame_types.length > 0 && (
                <div className="mt-2">
                  <span className="text-xs text-blue-600 font-medium">Frames: </span>
                  <span className="text-xs text-gray-600">{item.frame_types.join(', ')}</span>
                </div>
              )}
              {/* Show foundation types for hive types */}
              {item.foundation_types && item.foundation_types.length > 0 && (
                <div>
                  <span className="text-xs text-purple-600 font-medium">Foundations: </span>
                  <span className="text-xs text-gray-600">{item.foundation_types.join(', ')}</span>
                </div>
              )}
              {/* Show box configuration for hive types */}
              {item.requires_boxes && (
                <div className="mt-2">
                  <span className="text-xs text-amber-600 font-medium">📦 Stackable Boxes: </span>
                  {item.box_types && item.box_types.length > 0 ? (
                    <span className="text-xs text-gray-600">{item.box_types.map(b => b.name).join(', ')}</span>
                  ) : (
                    <span className="text-xs text-gray-400">None configured</span>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleToggleActive(item.id)}
              title={isInactive ? 'Activate' : 'Deactivate'}
            >
              {isInactive ? <Check className="w-4 h-4 text-green-600" /> : <X className="w-4 h-4 text-gray-400" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleEdit(item)}
            >
              <Pencil className="w-4 h-4" />
            </Button>
            {!item.is_default && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(item.id)}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderForm = () => {
    const fields = FIELD_CONFIGS[activeTab] || [];
    
    return (
      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="text-lg">
            {editingItem ? 'Edit Item' : 'Add New Item'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fields.map(field => {
              // Skip fields with unmet dependencies
              if (field.dependsOn && !formData[field.dependsOn]) {
                return null;
              }
              
              // Box config takes full width
              const isFullWidth = field.type === 'textarea' || field.type === 'box_config' || field.type === 'toggle_group';
              
              return (
                <div key={field.key} className={isFullWidth ? 'md:col-span-2' : ''}>
                  <Label className="mb-2 block">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </Label>
                  {renderField(field)}
                </div>
              );
            })}
          </div>
          
          <div className="flex gap-2 mt-6">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : (editingItem ? 'Update' : 'Create')}
            </Button>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  const activeTabConfig = CONFIG_TABS.find(t => t.id === activeTab);

  return (
    <div className="bg-gray-50 p-6">
      <div className="max-w-7xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
              <Settings className="h-6 w-6 text-amber-600" />
              Admin Configuration
            </h1>
            <p className="text-sm text-stone-500 mt-1">Manage app-wide settings and customizable options</p>
          </div>
          <Button
            variant="outline"
            onClick={async () => {
              try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${API_URL}/api/admin/manual/download`, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                if (response.ok) {
                  const blob = await response.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'Thrive_Admin_Manual.md';
                  a.click();
                  window.URL.revokeObjectURL(url);
                  toast.success('Admin Manual downloaded');
                } else {
                  toast.error('Failed to download manual');
                }
              } catch (err) {
                toast.error('Download failed');
              }
            }}
            className="hidden md:flex items-center gap-2"
          >
            <BookOpen className="w-4 h-4" />
            Admin Manual
            <Download className="w-4 h-4" />
          </Button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            {error}
          </div>
        )}

        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <div className="overflow-x-auto -mx-2 px-2 pb-2">
            <TabsList className="inline-flex min-w-max mb-4 bg-white border p-1 rounded-lg gap-0.5">
              {CONFIG_TABS.map(tab => {
                const Icon = tab.icon;
                const pendingCount = tab.id === 'task_types' ? pendingCounts.pending_tasks 
                  : tab.id === 'action_types' ? pendingCounts.pending_actions : 0;
                return (
                  <TabsTrigger
                    key={tab.id}
                    value={tab.id}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm whitespace-nowrap data-[state=active]:bg-amber-100 data-[state=active]:text-amber-800 relative"
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span>{tab.shortName}</span>
                    {pendingCount > 0 && (
                      <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                        {pendingCount}
                      </span>
                    )}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>

          {CONFIG_TABS.map(tab => (
            <TabsContent key={tab.id} value={tab.id}>
              {tab.id === 'harvest' ? (
                // Special Harvest Configuration UI
                <HarvestConfigPanel 
                  config={harvestConfig}
                  setConfig={setHarvestConfig}
                  loading={loading}
                  saving={harvestSaving}
                  onSave={saveHarvestConfig}
                />
              ) : tab.id === 'task_types' ? (
                // Special Task Types Configuration UI
                <TaskTypesPanel token={token} loading={loading} />
              ) : tab.id === 'action_types' ? (
                // Special Action Types Configuration UI
                <ActionTypesPanel token={token} loading={loading} />
              ) : tab.id === 'demo_videos' ? (
                // Special Demo Videos Configuration UI
                <DemoVideosPanel token={token} loading={loading} />
              ) : tab.id === 'tools' ? (
                // Special Tools Configuration UI
                <ToolsConfigPanel 
                  token={token} 
                  loading={loading} 
                  items={items} 
                  onRefresh={() => fetchItems('tools')}
                />
              ) : tab.id === 'components' ? (
                // Special Components Configuration UI
                <ComponentsConfigPanel 
                  token={token} 
                  loading={loading} 
                  items={items}
                  onRefresh={() => fetchItems('components')}
                />
              ) : tab.id === 'product_types' ? (
                // Product Types Configuration UI (for admins to manage product categories)
                <ProductTypesConfigPanel 
                  token={token} 
                  loading={loading}
                />
              ) : tab.id === 'beekeeper_tasks' ? (
                <BeekeeperTasksPanel token={token} />
              ) : tab.id === 'challenges' ? (
                <ChallengeManagerPanel token={token} />
              ) : (
                <>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          {React.createElement(tab.icon, { className: "w-5 h-5 text-amber-600" })}
                          {tab.name}
                        </CardTitle>
                        <CardDescription>{tab.description}</CardDescription>
                      </div>
                      {!isAdding && !editingItem && (
                        <Button onClick={handleAdd} className="bg-amber-600 hover:bg-amber-700">
                          <Plus className="w-4 h-4 mr-2" />
                          Add {tab.name.replace(/s$/, '')}
                        </Button>
                      )}
                    </CardHeader>
                    <CardContent>
                      {(isAdding || editingItem) && renderForm()}
                      
                      {!isAdding && !editingItem && (
                        <div className="space-y-3">
                          {loading ? (
                            <div className="text-center py-8 text-gray-500">Loading...</div>
                          ) : items.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                              No items configured. Click &quot;Add&quot; to create one.
                            </div>
                          ) : (
                            items.map(item => renderItemCard(item))
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Info card */}
                  <Card className="mt-4 bg-blue-50 border-blue-200">
                    <CardContent className="pt-4">
                      <div className="flex gap-3">
                        <div className="text-blue-600">ℹ️</div>
                        <div className="text-sm text-blue-800">
                          <p className="font-medium mb-1">How it works:</p>
                          <ul className="list-disc list-inside space-y-1 text-blue-700">
                            <li>Items you add here will appear in dropdowns throughout the app</li>
                            <li>Default items (marked with badge) cannot be deleted, but can be deactivated</li>
                            <li>Inactive items won&apos;t appear in user-facing forms</li>
                            <li>Changes take effect immediately across all forms</li>
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}
